import { parseMoneyToCents, sumCents, type MoneyInput } from '@agos/shared'

/**
 * How much cash a waiter hands over at the end of their shift.
 *
 * Everything they took, less everything that never became cash in their apron:
 * the card payments the customer made directly, the vouchers they accepted, the
 * things they bought for the restaurant out of the till, and anything they paid
 * out. What is left is what goes in the safe.
 *
 * Ported from legacy `components/waiter-handover-modal.tsx`, in integer cents
 * (§14.7). This number is counted against physical banknotes, so it is the one
 * figure on the screen that has to be exactly right.
 */

export interface WaiterWalletSummary {
  totalRevenue?: MoneyInput
  orderCount?: number
  cashTotal?: MoneyInput
  cardTotal?: MoneyInput
  cardTerminalTotal?: MoneyInput
  cardCourierTotal?: MoneyInput
  cardOnlineTotal?: MoneyInput
}

/** One typed line: an amount and what it was for. */
export interface HandoverLine {
  id: string
  amount: string
  note: string
}

export function lineTotalOf(lines: HandoverLine[]): number {
  return sumCents(...lines.map((line) => line.amount))
}

export interface HandoverTotals {
  totalRevenueCents: number
  cardOnlineCents: number
  cardTerminalCents: number
  cardCourierCents: number
  voucherCents: number
  purchaseCents: number
  payoutCents: number
  deductionCents: number
  cashToHandOverCents: number
}

export function handoverTotals({
  summary,
  vouchers,
  purchases,
  payouts,
}: {
  summary: WaiterWalletSummary | null
  vouchers: HandoverLine[]
  purchases: HandoverLine[]
  payouts: HandoverLine[]
}): HandoverTotals {
  // The three card fields are deducted individually, never `cardTotal` — that
  // field is their sum, and subtracting both would take the card money off
  // twice.
  const cardOnline = parseMoneyToCents(summary?.cardOnlineTotal)
  const cardTerminal = parseMoneyToCents(summary?.cardTerminalTotal)
  const cardCourier = parseMoneyToCents(summary?.cardCourierTotal)

  const voucher = lineTotalOf(vouchers)
  const purchase = lineTotalOf(purchases)
  const payout = lineTotalOf(payouts)

  const totalRevenue = parseMoneyToCents(summary?.totalRevenue)
  const deduction = cardOnline + cardTerminal + cardCourier + voucher + purchase + payout

  return {
    totalRevenueCents: totalRevenue,
    cardOnlineCents: cardOnline,
    cardTerminalCents: cardTerminal,
    cardCourierCents: cardCourier,
    voucherCents: voucher,
    purchaseCents: purchase,
    payoutCents: payout,
    deductionCents: deduction,
    // Deliberately not clamped at zero. A waiter who paid out more than they
    // took is owed money, and a figure that silently floored at zero would hide
    // that from the person counting the safe.
    cashToHandOverCents: totalRevenue - deduction,
  }
}

/**
 * The backend stores two fixed payout slots and two purchase slots, not a list.
 * Several typed lines collapse into the first slot with their notes joined, so
 * the detail survives as text even though the schema has nowhere structured to
 * put it.
 */
export function joinLineNotes(lines: HandoverLine[], formatAmount: (cents: number) => string): string {
  return lines
    .filter((line) => parseMoneyToCents(line.amount) > 0)
    .map((line) => {
      const amount = formatAmount(parseMoneyToCents(line.amount))
      return line.note ? `${line.note} (${amount})` : amount
    })
    .join(' + ')
}
